/**
 * Location Comparison
 * String comparison, spelling detection, edit distance calculation
 */

import { normalizeCompareStr } from '../utils/string-utils.js';

/**
 * Check if input is a ZIP code (numeric, possibly with dash)
 * @param {string} input
 * @returns {boolean}
 */
export function isZipCode(input) {
  if (!input || typeof input !== 'string') return false;
  // Remove spaces and check if it's numeric (possibly with dash like 12345-6789)
  const cleaned = input.trim().replace(/\s+/g, '');
  // Match 5 digits or 5 digits + dash + 4 digits (US ZIP code format)
  return /^\d{5}(-\d{4})?$/.test(cleaned);
}

/**
 * Check if raw input is likely the same location as corrected label
 * @param {string} rawInput
 * @param {string} correctedLabel
 * @param {string} correctedCity
 * @returns {boolean}
 */
export function isLikelySameLocation(rawInput, correctedLabel, correctedCity) {
  const raw = normalizeCompareStr(rawInput);
  const label = normalizeCompareStr(correctedLabel);
  const city = normalizeCompareStr(correctedCity);
  if (!raw || !label) return true;
  // If raw is contained in corrected label/city, treat as "not materially different"
  return label.includes(raw) || (city && city.includes(raw));
}

/**
 * Check if two location texts are effectively the same (e.g., "New York City" vs "New York")
 * This prevents showing unnecessary correction banners for minor text differences
 * @param {string} text1 - First text (user input)
 * @param {string} text2 - Second text (corrected from API)
 * @returns {boolean} - True if texts are effectively the same
 */
export function isLocationEffectivelySame(text1, text2) {
  if (!text1 || !text2) return false;

  // Normalize both texts: lowercase, trim, remove extra spaces
  const normalize = (text) => text.toLowerCase().trim().replace(/\s+/g, ' ');

  const normalized1 = normalize(text1);
  const normalized2 = normalize(text2);

  // Exact match after normalization
  if (normalized1 === normalized2) return true;

  // Remove common location suffixes and directional prefixes that don't change meaning
  const normalizeLocationText = (text) => {
    // Remove state code/name after comma (e.g., ", NY", ", California")
    let cleaned = text.replace(/,\s*[a-z]{2}\s*$/i, ''); // Remove ", NY", ", CA", etc.
    cleaned = cleaned.replace(/,\s*[a-z\s]+$/i, ''); // Remove ", State Name"

    // Remove common suffixes
    const suffixes = [
      /\s+city\s*$/i,
      /\s+town\s*$/i,
      /\s+borough\s*$/i,
      /\s+village\s*$/i,
      /\s+ville\s*$/i,
      /\s+beach\s*$/i,
      /\s+park\s*$/i,
    ];
    suffixes.forEach((suffix) => {
      cleaned = cleaned.replace(suffix, '');
    });

    // Remove directional prefixes (east, west, north, south) from beginning
    cleaned = cleaned.replace(/^(east|west|north|south)\s+/i, '');

    // Also remove directional words from anywhere if they're standalone
    // (e.g., "New York East" -> "New York")
    cleaned = cleaned.replace(/\s+(east|west|north|south)\s*$/i, '');

    return cleaned.trim();
  };

  const cleaned1 = normalizeLocationText(normalized1);
  const cleaned2 = normalizeLocationText(normalized2);

  // Match after removing suffixes
  if (cleaned1 === cleaned2) return true;

  // Check if one contains the other (e.g., "new york city" contains "new york")
  if (cleaned1.includes(cleaned2) || cleaned2.includes(cleaned1)) {
    // Make sure the shorter text is substantial (at least 3 chars)
    const shorter = cleaned1.length < cleaned2.length ? cleaned1 : cleaned2;
    if (shorter.length >= 3) {
      return true;
    }
  }

  // Split into words and check if all significant words from shorter text are in longer
  const words1 = cleaned1.split(/\s+/).filter((w) => w.length > 2); // Skip short words
  const words2 = cleaned2.split(/\s+/).filter((w) => w.length > 2);

  if (words1.length > 0 && words2.length > 0) {
    const shorterWords = words1.length <= words2.length ? words1 : words2;
    const longerWords = words1.length > words2.length ? words1 : words2;
    const allWordsMatch = shorterWords.every((word) =>
      longerWords.includes(word)
    );
    if (allWordsMatch && shorterWords.length >= 2) {
      return true; // All significant words match
    }
  }

  return false;
}

/**
 * Check if there are spelling mistakes between raw input and corrected location
 * Detects:
 * - 1 duplicated letter (recognized)
 * - 1 missing letter (recognized)
 * - 2 adjacent missing letters (recognized)
 * - 2-3 non-adjacent missing letters (may be recognized)
 * @param {string} rawInput - User's input
 * @param {string} correctedLabel - Corrected location label
 * @param {string} correctedCity - Corrected city name
 * @returns {boolean} - True if spelling mistakes are detected
 */
export function hasSpellingMistakes(rawInput, correctedLabel, correctedCity) {
  if (!rawInput || !correctedLabel) return false;

  const raw = normalizeCompareStr(rawInput);
  const label = normalizeCompareStr(correctedLabel);
  const city = normalizeCompareStr(correctedCity);

  // If strings are exactly the same, no mistakes
  if (raw === label || raw === city) return false;

  // Calculate edit distance for both label and city
  const editDistance = calculateEditDistance(raw, label);
  const cityEditDistance = city ? calculateEditDistance(raw, city) : Infinity;
  const minEditDistance = Math.min(editDistance, cityEditDistance);

  // If edit distance is 1-3, it's likely a spelling mistake
  // This covers: 1 duplicated letter, 1 missing letter, 2 adjacent missing letters, 2-3 non-adjacent missing letters
  if (minEditDistance >= 1 && minEditDistance <= 3) {
    // Check if strings are similar (one contains the other or they share a common prefix)
    // This helps distinguish typos from completely different locations
    const rawInLabel = label.includes(raw) || raw.includes(label);
    const rawInCity = city && (city.includes(raw) || raw.includes(city));
    const lengthDiff = Math.min(
      Math.abs(raw.length - label.length),
      city ? Math.abs(raw.length - city.length) : Infinity
    );

    // If strings are similar (one contains the other or length difference is small), it's a typo
    if (
      rawInLabel ||
      rawInCity ||
      (lengthDiff <= 3 && minEditDistance <= 3)
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Calculate Levenshtein edit distance between two strings
 * @param {string} str1
 * @param {string} str2
 * @returns {number}
 */
export function calculateEditDistance(str1, str2) {
  const len1 = str1.length;
  const len2 = str2.length;

  // Create a matrix
  const matrix = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(0));

  // Initialize first row and column
  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;

  // Fill the matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // deletion
        matrix[i][j - 1] + 1, // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return matrix[len1][len2];
}

console.log('📦 Location comparison module loaded');
